import moment from 'moment-timezone'
import {
  getSchedulableDatesFromList,
  executeSchedulingRequest,
  SchedulingResultType,
} from '@abroad/rest-client'
import { convertDateAndSlotToUTC } from './sessionDateUtils'

/**
 * Handle reschedule session API call
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} API response
 */
export const handleRescheduleSession = async ({
  eventId,
  coachId,
  selectedDate,
  selectedSlot,
  scheduledSession,
  clientTimezone,
  onSuccess,
  onError,
  API,
  Notification,
  history,
  onScheduleSuccess,
}) => {
  if (!eventId || !coachId) {
    Notification.showNotification(
      'Unable to find session details. Please try again later.',
      'danger',
    )
    return
  }

  // Convert selected date + slot time to UTC (accounting for user's timezone)
  const { start: slotStartMoment, end: slotEndFromConversion } =
    convertDateAndSlotToUTC(
      selectedDate,
      selectedSlot.start,
      selectedSlot.end,
      clientTimezone,
    )

  const eventDurationMinutes = Number(scheduledSession?.duration)
  const slotEndMoment = eventDurationMinutes
    ? slotStartMoment.clone().add(eventDurationMinutes, 'minutes')
    : slotEndFromConversion || slotStartMoment.clone()

  const payload = {
    eventId,
    slotStart: slotStartMoment.toISOString(),
    slotEnd: slotEndMoment.toISOString(),
  }
  if (clientTimezone) {
    payload.clientTimezone = clientTimezone
  }

  const result = await executeSchedulingRequest(
    (schedulePayload) =>
      API.session.rescheduleSession(coachId, schedulePayload),
    payload,
  )

  if (result.type === SchedulingResultType.SAME_DAY_CONFLICT) {
    Notification.showNotification(result.message, 'danger')
    return { success: false, sameDayConflict: true }
  }

  if (result.type === SchedulingResultType.WEEKLY_WARNING) {
    return { success: false, weeklyWarning: true, message: result.message }
  }

  if (result.type !== SchedulingResultType.SUCCESS) {
    Notification.showNotification(
      result.message || 'Unable to reschedule your session. Please try again.',
      'danger',
    )
    return { success: false }
  }

  const data = result.data

  Notification.showNotification(
    'Your session has been rescheduled successfully!',
    'success',
  )

  if (onScheduleSuccess) {
    onScheduleSuccess(data?.event)
  } else {
    history.push('/coaching/sessions', {
      rescheduledEvent: data?.event,
    })
  }

  return { success: true, data }
}

/**
 * Handle first session scheduling API call
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} API response
 */
export const handleScheduleFirstSession = async ({
  selectedDate,
  selectedSlot,
  sowId,
  sowSessionDuration,
  clientTimezone,
  API,
  Notification,
  history,
  onScheduleSuccess,
  runScheduling,
}) => {
  const { start: slotStartMoment, end: slotEndFromConversion } =
    convertDateAndSlotToUTC(
      selectedDate,
      selectedSlot.start,
      selectedSlot.end,
      clientTimezone,
    )

  const sessionDurationMinutes = Number(sowSessionDuration) || 0
  const slotEndMoment = sessionDurationMinutes
    ? slotStartMoment.clone().add(sessionDurationMinutes, 'minutes')
    : slotEndFromConversion || slotStartMoment.clone()

  const payload = {
    slotStartTime: slotStartMoment.toISOString(),
    slotEndTime: slotEndMoment.toISOString(),
    sowId,
  }

  const result = await runScheduling(
    (schedulePayload) => API.session.scheduleFirstSession(schedulePayload),
    payload,
  )

  if (!result?.success) {
    return result
  }

  return result.data
}

/**
 * Handle remaining sessions scheduling API call
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} API response
 */
export const handleScheduleRemainingSessions = async ({
  selectedDate,
  selectedSlot,
  sowId,
  clientTimezone,
  API,
  Notification,
  history,
  onScheduleSuccess,
}) => {
  const { start: slotStartMoment } = convertDateAndSlotToUTC(
    selectedDate,
    selectedSlot.start,
    null,
    clientTimezone,
  )

  const payload = {
    slotStartTime: slotStartMoment.toISOString(),
    sowId,
  }

  const { data } = await API.session.scheduleRemainingSessions(payload)

  if (!data?.success) {
    Notification.showNotification(
      data?.message || 'Unable to schedule your sessions. Please try again.',
      'danger',
    )
    return
  }

  Notification.showNotification(
    'Your remaining sessions have been scheduled!',
    'success',
  )

  if (onScheduleSuccess) {
    onScheduleSuccess(data?.event)
  } else {
    history.push('/coaching/sessions', {
      scheduledEvent: data?.event,
    })
  }

  return data
}

/**
 * Handle schedule all sessions API call
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} API response
 */
export const handleScheduleAllSessions = async ({
  selectedDatesList,
  sowId,
  sowSessionDuration,
  clientTimezone,
  API,
  Notification,
  history,
  onScheduleSuccess,
  runScheduling,
  isConditionalSchedulingEnabled = false,
}) => {
  if (!selectedDatesList || selectedDatesList.length === 0) {
    Notification.showNotification(
      'Please select dates for scheduling.',
      'danger',
    )
    return
  }

  const availableDates = getSchedulableDatesFromList(selectedDatesList)

  if (availableDates.length === 0) {
    Notification.showNotification(
      'No available slots found. Please select different dates.',
      'danger',
    )
    return
  }

  const timeSlotsPayload = availableDates.map((dateItem) => {
    const startMoment = moment.utc(dateItem.slotStart)
    // Growth slots come back from availability with their own duration
    const durationMinutes =
      Number(dateItem.duration) || Number(sowSessionDuration) || 0
    const endMoment = durationMinutes
      ? startMoment.clone().add(durationMinutes, 'minutes')
      : dateItem.slotEnd
      ? moment.utc(dateItem.slotEnd)
      : startMoment.clone()

    const payloadItem = {
      date: dateItem.date,
      startTime: startMoment.toISOString(),
      endTime: endMoment.toISOString(),
    }

    // Only sent when conditional scheduling is on
    if (
      isConditionalSchedulingEnabled &&
      dateItem.sessionIndex !== undefined &&
      dateItem.sessionIndex !== null
    ) {
      payloadItem.sessionIndex = Number(dateItem.sessionIndex)
    }

    return payloadItem
  })

  const payload = {
    sowId,
    timeSlots: timeSlotsPayload,
  }

  if (clientTimezone) {
    payload.clientTimezone = clientTimezone
  }

  const result = await runScheduling(
    (schedulePayload) => API.session.scheduleAllSessions(schedulePayload),
    payload,
  )

  if (!result?.success) {
    return result
  }

  return result.data
}
